import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCourse } from '../../context/CourseContext';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Headphones,
  X,
} from 'lucide-react';

export const AudioPlayer = ({ chapter, minimized = false }) => {
  const { isAudioPlaying, toggleAudio, audioProgress, setAudioProgress } = useCourse();
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  // Simulate audio progress when playing
  useEffect(() => {
    let interval;
    if (isAudioPlaying) {
      interval = setInterval(() => {
        setAudioProgress((prev) => {
          if (prev >= 100) {
            toggleAudio();
            return 0;
          }
          return prev + 0.5;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isAudioPlaying, setAudioProgress, toggleAudio]);

  const formatTime = (progress, totalMinutes) => {
    const totalSeconds = (totalMinutes * 60 * progress) / 100;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const parseAudioLength = (audioLength) => {
    const match = audioLength.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 30;
  };

  const totalMinutes = parseAudioLength(chapter.audioLength);

  // Waveform visualization
  const WaveformBars = () => (
    <div className="audio-waveform">
      {Array.from({ length: 20 }).map((_, i) => {
        const height = isAudioPlaying 
          ? Math.random() * 100 
          : 20 + Math.sin(i * 0.5) * 15;
        return (
          <motion.div
            key={`waveform-bar-${i}`}
            className="audio-bar"
            animate={{ height: `${height}%` }}
            transition={{ duration: 0.15 }}
          />
        );
      })}
    </div>
  );

  if (minimized) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed bottom-4 right-4 z-40"
      >
        <Button
          onClick={() => setShowPlayer(true)}
          className="btn-gold rounded-full w-14 h-14 shadow-gold"
        >
          <Headphones className="w-6 h-6" />
        </Button>

        <AnimatePresence>
          {showPlayer && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="absolute bottom-16 right-0 w-80 bg-card border rounded-xl shadow-xl p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Headphones className="w-5 h-5 text-primary" />
                  <span className="font-medium text-sm">Audio Narration</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8"
                  onClick={() => setShowPlayer(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-xs text-muted-foreground mb-3 truncate">
                {chapter.title}
              </p>

              <WaveformBars />

              <div className="mt-4">
                <Slider
                  value={[audioProgress]}
                  max={100}
                  step={0.1}
                  onValueChange={(value) => setAudioProgress(value[0])}
                  className="mb-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTime(audioProgress, totalMinutes)}</span>
                  <span>{chapter.audioLength}</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 mt-4">
                <Button variant="ghost" size="icon" className="w-10 h-10">
                  <SkipBack className="w-5 h-5" />
                </Button>
                <Button
                  onClick={toggleAudio}
                  className="btn-teal w-12 h-12 rounded-full"
                >
                  {isAudioPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" className="w-10 h-10">
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-silver-shimmer/20 to-background rounded-xl p-6 border border-silver/20 shadow-silver">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-maroon/10 flex items-center justify-center shadow-maroon">
          <Headphones className="w-6 h-6 text-maroon" />
        </div>
        <div>
          <h4 className="font-medium text-foreground">Audio Narration</h4>
          <p className="text-sm text-muted-foreground">Listen while you follow along</p>
        </div>
        <Badge variant="outline" className="ml-auto border-silver text-muted-foreground">
          {chapter.audioLength}
        </Badge>
      </div>

      {/* Waveform */}
      <div className="mb-4">
        <WaveformBars />
      </div>

      {/* Progress Slider */}
      <div className="mb-4">
        <Slider
          value={[audioProgress]}
          max={100}
          step={0.1}
          onValueChange={(value) => setAudioProgress(value[0])}
          className="mb-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatTime(audioProgress, totalMinutes)}</span>
          <span>{chapter.audioLength}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMuted(!isMuted)}
            className="w-10 h-10"
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </Button>
          <Slider
            value={[isMuted ? 0 : volume]}
            max={100}
            step={1}
            onValueChange={(value) => {
              setVolume(value[0]);
              if (value[0] > 0) setIsMuted(false);
            }}
            className="w-24"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="w-10 h-10">
            <SkipBack className="w-5 h-5" />
          </Button>
          <Button
            onClick={toggleAudio}
            className="btn-teal w-14 h-14 rounded-full"
          >
            {isAudioPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-0.5" />
            )}
          </Button>
          <Button variant="ghost" size="icon" className="w-10 h-10">
            <SkipForward className="w-5 h-5" />
          </Button>
        </div>

        <div className="w-[88px]" /> {/* Spacer for balance */}
      </div>

      <p className="text-center text-xs text-muted-foreground mt-4">
        🎧 Audio narration is simulated for this prototype
      </p>
    </div>
  );
};

export default AudioPlayer;
